import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "~assets/frontend/elements/icon/Icon";
import { motion, useMotionValue } from "framer-motion";
import Translator from "bazinga-translator";
import { debounce } from "~assets/frontend/_util/debounce";
import { SliderInput } from "~assets/frontend/components/comp-range_slider/slider-input";
import { dispatchTrackingEvent } from "~assets/global/tracking/tracking-event";
import { getId } from "~assets/frontend/_util/getId";
export const RangeSlider = (props) => {
    const [pixelSliderWidth, setPixelSliderWidth] = useState(1);
    const rangeRef = useRef(null);
    const lowerConstraint = useRef(null);
    const upperConstraint = useRef(null);
    const [valueMin, setValueMin] = useState(props.valueMin ? props.valueMin.toString() : props.min.toString());
    const [valueMax, setValueMax] = useState(props.valueMax ? props.valueMax.toString() : props.max.toString());
    const digits = props.digits === undefined ? 2 : props.digits;
    const initSlider = () => {
        if (rangeRef.current instanceof HTMLDivElement) {
            const sliderWidth = rangeRef.current.clientWidth === 0 ? 100 : rangeRef.current.clientWidth;
            setPixelSliderWidth(sliderWidth);
            pixelMinimum.set(convertToPixel(props.valueMin || props.min));
            pixelMaximum.set(convertToPixel(props.valueMax || props.max));
        }
    };
    useEffect(() => {
        window.addEventListener("resize", initSlider);
        return () => {
            window.removeEventListener("resize", initSlider);
        };
    }, []);
    useEffect(() => {
        if (props.onChange) {
            props.onChange(valueMin, valueMax);
        }
    }, [valueMin, valueMax]);
    const convertToPixel = (value) => {
        return ((value - props.min) / (props.max - props.min)) * pixelSliderWidth;
    };
    const convertToValue = (pixel) => {
        const value = pixel / pixelSliderWidth;
        const preciseValue = value * (props.max - props.min) + props.min;
        if (preciseValue < props.min) {
            return props.min;
        }
        if (preciseValue > props.max) {
            return props.max;
        }
        // modulo with float does not work
        if (digits > 0) {
            const multiplier = Math.pow(10, digits + 1);
            const stepper = props.step * multiplier;
            const integer = Math.round(preciseValue * multiplier);
            const modulo = integer % stepper;
            if (modulo >= stepper / 2) {
                return (integer + stepper - modulo) / multiplier;
            }
            else {
                return (integer - modulo) / multiplier;
            }
        }
        const modulo = preciseValue % props.step;
        if (modulo >= props.step / 2) {
            return preciseValue + props.step - modulo;
        }
        else {
            return preciseValue - modulo;
        }
    };
    const pixelMinimum = useMotionValue(0);
    const pixelMaximum = useMotionValue(1);
    const pixelLineRight = useMotionValue(pixelMaximum.get());
    useEffect(() => {
        pixelLineRight.set(pixelSliderWidth - pixelMaximum.get());
    }, [pixelMaximum.get(), pixelSliderWidth]);
    useEffect(() => {
        const unsubscribeX = pixelMinimum.on("change", updateMin);
        const unsubscribeY = pixelMaximum.on("change", updateMax);
        function updateMin() {
            const value = convertToValue(pixelMinimum.get());
            pixelMinimum.set(convertToPixel(value));
            setValueMin(value.toFixed(digits));
        }
        function updateMax() {
            const value = convertToValue(pixelMaximum.get());
            pixelMaximum.set(convertToPixel(value));
            setValueMax(value.toFixed(digits));
        }
        return () => {
            unsubscribeX();
            unsubscribeY();
        };
    }, [pixelSliderWidth, props.max, props.min]);
    useEffect(() => {
        initSlider();
    }, [rangeRef, pixelSliderWidth]);
    const debouncedPixelValueMin = useMemo(() => {
        return debounce((value) => {
            trackSelection("input");
            pixelMinimum.set(convertToPixel(parseFloat(value)));
        }, 400);
    }, [pixelSliderWidth, props.max, props.min]);
    const debouncedPixelValueMax = useMemo(() => {
        return debounce((value) => {
            trackSelection("input");
            pixelMaximum.set(convertToPixel(parseFloat(value)));
        }, 400);
    }, [pixelSliderWidth, props.max, props.min]);
    const trackSelection = (target) => {
        dispatchTrackingEvent({
            name: "ui",
            payload: {
                ui_component: "comp-range-slider",
                ui_function: "select",
                ui_action: "value",
                ui_target: target,
            },
        });
    };
    const lowerConstraintWidth = pixelMaximum.get();
    const upperConstraintWidth = pixelSliderWidth - pixelMinimum.get();
    return (React.createElement("div", { className: "slider-group mb-24" },
        React.createElement("p", { className: "py-6" }, props.label),
        React.createElement("div", { className: "relative mx-36 flex h-60 flex-row items-center py-12 ", ref: rangeRef },
            React.createElement(motion.div, { style: { width: pixelMinimum }, className: "absolute -left-36 h-[1px] bg-anthracite-30" }),
            React.createElement(motion.div, { style: { width: pixelLineRight }, className: "absolute -right-36 h-[1px] bg-anthracite-30" }),
            React.createElement("span", { className: "h-[1px] w-full bg-anthracite-80" }),
            React.createElement(motion.div, { role: "slider", className: "lower absolute -left-36  h-36 w-36 border-1 border-transparent hover:cursor-pointer", drag: "x", dragConstraints: lowerConstraint, dragElastic: { left: 0.1, right: 0.1 }, dragMomentum: false, dragListener: true, onDragEnd: () => {
                    trackSelection("thumb");
                }, "aria-valuemin": props.min, "aria-valuemax": parseFloat(valueMax), "aria-valuenow": parseFloat(valueMin), "aria-valuetext": props.unit ? valueMin + props.unit : undefined, style: { x: pixelMinimum }, "aria-label": Translator.trans("comp-range_slider.aria-label.min", {}, "frontend") },
                React.createElement("div", { className: "flex h-full w-full items-center justify-center bg-anthracite-80" },
                    React.createElement(Icon, { _id: getId(), _name: "IconStruct", identifier: "icon-arrow-right", className: "before:bg-white" }))),
            React.createElement(motion.div, { role: "slider", className: "upper absolute left-0  h-36 w-36  border-1 border-transparent hover:cursor-pointer", drag: "x", dragConstraints: upperConstraint, dragElastic: { left: 0.1, right: 0.1 }, onDragEnd: () => {
                    trackSelection("thumb");
                }, dragMomentum: false, dragListener: true, "aria-valuemin": parseFloat(valueMin), "aria-valuemax": props.max, "aria-valuenow": parseFloat(valueMax), "aria-valuetext": props.unit ? valueMax + props.unit : undefined, style: { x: pixelMaximum }, "aria-label": Translator.trans("comp-range_slider.aria-label.max", {}, "frontend") },
                React.createElement("div", { className: "flex h-full w-full items-center justify-center bg-anthracite-80" },
                    React.createElement(Icon, { _id: getId(), _name: "IconStruct", identifier: "icon-arrow-left", className: "before:bg-white" }))),
            React.createElement("div", { ref: lowerConstraint, className: "absolute -left-36 -z-10", style: { width: lowerConstraintWidth + 36 } }),
            React.createElement("div", { ref: upperConstraint, className: "absolute -right-36 -z-10", style: { width: upperConstraintWidth + 36 } })),
        React.createElement("div", { className: "flex flex-row justify-between" },
            React.createElement(SliderInput, { value: valueMin, unit: props.unit, ariaLabel: Translator.trans("comp-range_slider.aria-label.min", {}, "frontend"), onChange: (value) => {
                    setValueMin(value);
                    if (parseFloat(value) <= parseFloat(valueMax)) {
                        debouncedPixelValueMin(value);
                    }
                    else {
                        debouncedPixelValueMin(valueMax);
                    }
                } }),
            React.createElement(SliderInput, { value: valueMax, unit: props.unit, ariaLabel: Translator.trans("comp-range_slider.aria-label.max", {}, "frontend"), onChange: (value) => {
                    setValueMax(value);
                    if (parseFloat(value) >= parseFloat(valueMin)) {
                        debouncedPixelValueMax(value);
                        trackSelection("thumb");
                    }
                    else {
                        debouncedPixelValueMax(valueMin);
                    }
                } }))));
};
